import { useId } from "react";
import { defineStrings, useStrings } from "../i18n";
import type { Preferences } from "../prefs";

const strings = defineStrings(
  {
    preview: "Preview",
    sample: "Fractions: which is bigger, 3/4 or 5/8?",
    colours: "Colours",
    text: "Text",
    movement: "Movement",
    theme: "Theme",
    "theme.system": "Auto",
    "theme.light": "Light",
    "theme.dark": "Dark",
    contrast: "Contrast",
    "contrast.system": "Auto",
    "contrast.standard": "Normal",
    "contrast.more": "Strong",
    font: "Letters",
    "font.standard": "Classic",
    "font.dyslexia": "Roomy",
    "font.opendyslexic": "Dyslexia",
    "font.standard.hint": "Clear letters: I, l and 1 never look alike.",
    "font.dyslexia.hint": "Lexend: wider letters with more space between them.",
    "font.opendyslexic.hint": "OpenDyslexic: weighted letters that stay the right way up.",
    textSize: "Size",
    "textSize.standard": "Normal",
    "textSize.large": "Large",
    motion: "Animations",
    "motion.system": "Auto",
    "motion.reduce": "Fewer",
    "motion.full": "All",
    auto: "like my device",
  },
  {
    preview: "Aperçu",
    sample: "Fractions : laquelle est la plus grande, 3/4 ou 5/8 ?",
    colours: "Couleurs",
    text: "Texte",
    movement: "Mouvement",
    theme: "Thème",
    "theme.system": "Auto",
    "theme.light": "Clair",
    "theme.dark": "Sombre",
    contrast: "Contraste",
    "contrast.system": "Auto",
    "contrast.standard": "Normal",
    "contrast.more": "Fort",
    font: "Lettres",
    "font.standard": "Classique",
    "font.dyslexia": "Aérée",
    "font.opendyslexic": "Dyslexie",
    "font.standard.hint": "Des lettres nettes : I, l et 1 ne se ressemblent jamais.",
    "font.dyslexia.hint": "Lexend : des lettres plus larges, plus d’espace entre elles.",
    "font.opendyslexic.hint": "OpenDyslexic : des lettres lestées qui restent dans le bon sens.",
    textSize: "Taille",
    "textSize.standard": "Normale",
    "textSize.large": "Grande",
    motion: "Animations",
    "motion.system": "Auto",
    "motion.reduce": "Moins",
    "motion.full": "Toutes",
    auto: "comme mon appareil",
  },
);

type Key = keyof typeof strings.en;

interface Row {
  readonly key: keyof Preferences;
  readonly options: readonly (readonly [string, Key])[];
}

const groups: readonly { readonly title: Key; readonly rows: readonly Row[] }[] = [
  {
    title: "colours",
    rows: [
      {
        key: "theme",
        options: [
          ["system", "theme.system"],
          ["light", "theme.light"],
          ["dark", "theme.dark"],
        ],
      },
      {
        key: "contrast",
        options: [
          ["system", "contrast.system"],
          ["standard", "contrast.standard"],
          ["more", "contrast.more"],
        ],
      },
    ],
  },
  {
    title: "text",
    rows: [
      {
        key: "font",
        options: [
          ["standard", "font.standard"],
          ["dyslexia", "font.dyslexia"],
          ["opendyslexic", "font.opendyslexic"],
        ],
      },
      {
        key: "textSize",
        options: [
          ["standard", "textSize.standard"],
          ["large", "textSize.large"],
        ],
      },
    ],
  },
  {
    title: "movement",
    rows: [
      {
        key: "motion",
        options: [
          ["system", "motion.system"],
          ["reduce", "motion.reduce"],
          ["full", "motion.full"],
        ],
      },
    ],
  },
];

const fontHints = {
  standard: "font.standard.hint",
  dyslexia: "font.dyslexia.hint",
  opendyslexic: "font.opendyslexic.hint",
} as const satisfies Record<Preferences["font"], Key>;

/**
 * The reading and display settings, meant for a `Sheet` (which carries the title): a
 * live preview, then Colours (theme, contrast), Text (letters, size) and Movement, each a
 * one-line segmented control of native radios (arrow keys, screen readers). Labels stay
 * one or two words; the letters row shows each option in its own face and explains the
 * chosen one underneath.
 */
export function ReadingPreferences(props: {
  value: Preferences;
  onChange: (next: Preferences) => void;
}) {
  const t = useStrings(strings);
  const id = useId();
  return (
    <div className="ui-prefs">
      <figure className="ui-prefs__preview">
        <span className="ui-prefs__preview-glyph" aria-hidden="true">
          Aa
        </span>
        <figcaption className="visually-hidden">{t("preview")}</figcaption>
        <p className="ui-prefs__preview-text">{t("sample")}</p>
      </figure>
      {groups.map((group) => (
        <section
          key={group.title}
          className="ui-prefs__group"
          aria-labelledby={`${id}-${group.title}`}
        >
          <h3 id={`${id}-${group.title}`} className="ui-prefs__group-title">
            {t(group.title)}
          </h3>
          {group.rows.map((row) => {
            const hintId = row.key === "font" ? `${id}-font-hint` : undefined;
            return (
              <fieldset key={row.key} className="ui-segmented" aria-describedby={hintId}>
                <legend className={group.rows.length === 1 ? "visually-hidden" : undefined}>
                  {t(row.key)}
                </legend>
                <div
                  className="ui-segmented__options"
                  style={{
                    gridTemplateColumns: `repeat(${String(row.options.length)}, minmax(0, 1fr))`,
                  }}
                >
                  {row.options.map(([option, label]) => (
                    <label
                      key={option}
                      className="ui-segmented__option"
                      data-font={row.key === "font" ? option : undefined}
                    >
                      <input
                        type="radio"
                        name={`${id}-${row.key}`}
                        value={option}
                        checked={props.value[row.key] === option}
                        onChange={() => {
                          props.onChange({ ...props.value, [row.key]: option });
                        }}
                      />
                      <span>{t(label)}</span>
                      {option === "system" && (
                        <span className="visually-hidden"> ({t("auto")})</span>
                      )}
                    </label>
                  ))}
                </div>
                {hintId !== undefined && (
                  <p id={hintId} className="ui-segmented__hint">
                    {t(fontHints[props.value.font])}
                  </p>
                )}
              </fieldset>
            );
          })}
        </section>
      ))}
    </div>
  );
}
